import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import api from "../../api/config";
import { toast } from "react-toastify";
import { Form, Input, Button, Upload, Select, Card, Typography, Row, Col, Checkbox, Radio } from "antd";
import { UserOutlined, MailOutlined, PhoneOutlined, GlobalOutlined, LockOutlined, PlusOutlined, EnvironmentOutlined, FileTextOutlined, BankOutlined, DollarOutlined, CalendarOutlined, TeamOutlined, HomeOutlined, SafetyOutlined } from "@ant-design/icons";

const InvestorRegister = ({ editingInvestor = null, isAdminEdit = false, onSuccess = null }) => {
  const [formData, setFormData] = useState({
    investorType: "individual", // individual or company
    // Individual fields
    fullName: "",
    designation: "",
    // Company fields
    companyName: "",
    registrationNumber: "",
    yearFounded: new Date().getFullYear(),
    // Common fields
    email: "",
    phone: "",
    country: "",
    city: "",
    address: "",
    website: "",
    investmentFocus: [],
    investmentStage: [],
    ticketSize: "",
    preferredIndustries: [],
    about: "",
    password: "",
    password2: "",
    profilePicture: null,
    companyLogo: null,
    identificationDocument: null,
    latitude: 20.9374, // Default to Vidarbha region
    longitude: 77.7796,
  });

  const [loading, setLoading] = useState(false);
  const [profilePreview, setProfilePreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [documentName, setDocumentName] = useState("");
  const [industries, setIndustries] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const navigate = useNavigate();

  const passwordStrength =
    formData.password.length >= 8
      ? "strong"
      : formData.password.length >= 6
      ? "medium"
      : "weak";

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch industries
        const industriesRes = await axios.get("/api/industries");
        const industriesData = industriesRes?.data?.data || industriesRes?.data;
        if (Array.isArray(industriesData)) {
          setIndustries(industriesData);
        } else {
          console.error("Invalid industries data format:", industriesRes?.data);
          setIndustries([]);
          toast.error("Invalid industries data format");
        }

      } catch (err) {
        console.error("Error fetching data:", err);
        setIndustries([]);
        toast.error("Failed to load required data");
      }
    };

    fetchData();
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (editingInvestor && isAdminEdit) {
      setFormData({
        investorType: editingInvestor.investorType || "individual",
        fullName: editingInvestor.fullName || "",
        designation: editingInvestor.designation || "",
        companyName: editingInvestor.companyName || "",
        registrationNumber: editingInvestor.registrationNumber || "",
        yearFounded: editingInvestor.yearFounded || new Date().getFullYear(),
        email: editingInvestor.email || "",
        phone: editingInvestor.phone || "",
        country: editingInvestor.country || "",
        city: editingInvestor.city || "",
        address: editingInvestor.address || "",
        website: editingInvestor.website || "",
        investmentFocus: editingInvestor.investmentFocus || [],
        investmentStage: Array.isArray(editingInvestor.investmentStage) 
          ? editingInvestor.investmentStage 
          : [editingInvestor.investmentStage || ""],
        ticketSize: editingInvestor.ticketSize || "",
        preferredIndustries: (editingInvestor.preferredIndustries || [])
          .flat(Infinity)
          .map(ind => (ind && typeof ind === 'object' ? ind._id : ind))
          .filter(ind => typeof ind === 'string' && /^[0-9a-fA-F]{24}$/.test(ind)),
        about: editingInvestor.about || "",
        password: "",
        password2: "",
        profilePicture: null,
        companyLogo: null,
        identificationDocument: null,
        latitude: editingInvestor.latitude || 20.9374,
        longitude: editingInvestor.longitude || 77.7796,
      });

      // Set image previews
      if (editingInvestor.profilePicture) {
        let imageUrl = editingInvestor.profilePicture;
        if (!imageUrl.startsWith('http')) {
          if (imageUrl.startsWith('/')) {
            imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${imageUrl}`;
          } else {
            imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/${imageUrl}`;
          }
        }
        setProfilePreview(imageUrl);
      }

      if (editingInvestor.companyLogo) {
        let imageUrl = editingInvestor.companyLogo;
        if (!imageUrl.startsWith('http')) {
          if (imageUrl.startsWith('/')) {
            imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${imageUrl}`;
          } else {
            imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/${imageUrl}`;
          }
        }
        setLogoPreview(imageUrl);
      }

      if (editingInvestor.identificationDocument) {
        const fileName = editingInvestor.identificationDocument.split('/').pop();
        setDocumentName(fileName);
      }
    }
  }, [editingInvestor, isAdminEdit]);

  const handleLocationSelect = ({ latitude, longitude }) => {
    setFormData(prev => ({ ...prev, latitude, longitude }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === 'file') {
      const file = files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (name === 'profilePicture') {
            setProfilePreview(reader.result);
          } else if (name === 'companyLogo') {
            setLogoPreview(reader.result);
          } else if (name === 'identificationDocument') {
            setDocumentName(file.name);
          }
        };
        reader.readAsDataURL(file);
        setFormData(prev => ({ ...prev, [name]: file }));
      }
    }
    // Handle checkboxes for arrays
    else if (type === 'checkbox') {
      setFormData(prev => {
        const newArray = checked
          ? [...prev[name], value]
          : prev[name].filter(item => item !== value);
        return { ...prev, [name]: newArray };
      });
    }
    // Handle all other inputs
    else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Skip password validation in edit mode
    if (!isAdminEdit) {
      // Validate passwords match
      if (formData.password !== formData.password2) {
        toast.error('Passwords do not match');
        return;
      }
    }

    // Validate required fields based on investor type
    if (formData.investorType === 'individual') {
      if (!formData.fullName || !formData.email || !formData.phone) {
        toast.error('Please fill in all required fields');
        return;
      }
    } else {
      if (!formData.companyName || !formData.email || !formData.phone) {
        toast.error('Please fill in all required fields');
        return;
      }
    }

    // Skip document validation in edit mode
    if (!isAdminEdit && !formData.identificationDocument) {
      toast.error('Please upload an identification document');
      return;
    }

    setLoading(true);

    const formDataToSend = new FormData();
    
    // Derive investorName and contactPerson from form data
    const investorName = formData.investorType === 'company' ? formData.companyName : formData.fullName;
    const contactPerson = formData.investorType === 'company' ? formData.fullName : formData.fullName;
    
    formDataToSend.append('investorName', investorName);
    formDataToSend.append('contactPerson', contactPerson);
    
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && formData[key] !== '') {
        // Skip password fields in edit mode
        if (isAdminEdit && (key === 'password' || key === 'password2')) {
          return;
        }
        
        if (Array.isArray(formData[key])) {
          formData[key].forEach(item => formDataToSend.append(key, item));
        } else if (formData[key] instanceof File) {
          formDataToSend.append(key, formData[key]);
        } else {
          formDataToSend.append(key, formData[key]);
        }
      }
    });

    try {
      if (isAdminEdit && editingInvestor) {
        // Update existing investor
        const response = await api.put(`/admin/investors/${editingInvestor._id}`, formDataToSend);
        toast.success('Investor updated successfully');
        if (onSuccess) onSuccess();
      } else {
        // Register new investor
        // Don't set Content-Type manually — axios sets it automatically for
        // FormData with the correct multipart boundary.
        const config = {
          withCredentials: true,
          timeout: 30000,
        };

        const response = await axios.post('/api/auth/register/investor', formDataToSend, config);

        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('userInfo', JSON.stringify(response.data.user));
        }

        toast.success('Registration successful! Welcome to our platform.');
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Registration/Update error:', err);

      if (err.response?.data?.errors) {
        err.response.data.errors.forEach(error => {
          toast.error(error.msg || 'An error occurred');
        });
      } else if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else if (err.request) {
        toast.error('No response from server. Please try again later.');
      } else {
        toast.error('Error setting up request. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate year options for founded year (last 50 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 51 }, (_, i) => currentYear - i);

  // Investment stage options
  const investmentStageOptions = [
    'Pre-seed', 'Seed', 'Series A', 'Series B', 'Series C+', 'Growth', 'Late Stage'
  ];

  // Ticket size options
  const ticketSizeOptions = [
    'Under ₹25 Lakhs', '₹25 Lakhs - ₹50 Lakhs', '₹50 Lakhs - ₹1 Crore',
    '₹1 Crore - ₹5 Crore', '₹5 Crore - ₹10 Crore', 'Over ₹10 Crore'
  ];

  // Get industry name by ID
  const getIndustryName = (id) => {
    const industry = industries.find(ind => ind._id === id);
    return industry ? (industry.sectorName || industry.name) : id;
  };

  // Ant Design styling
  const baseStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#333",
  };

  const labelStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "15px",
    fontWeight: 400,
    color: "#333",
    marginBottom: "6px",
  };

  const sectionStyle = {
    background: "#fafafa",
    border: "1px solid #f0f0f0",
    borderRadius: 8,
    padding: 20,
    marginBottom: 20,
  };

  const sectionHeaderStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
  };

  const SectionTitle = ({ icon: Icon, children }) => (
    <div style={sectionHeaderStyle}>
      <Icon style={{ color: "#470172", fontSize: 18 }} />
      <Typography.Text strong style={{ ...baseStyle, fontSize: 17, color: "#470172" }}>
        {children}
      </Typography.Text>
    </div>
  );

  // Card-style section (matches the "Personal Information" reference design)
  const cardSectionStyle = {
    background: "#fff",
    border: "1px solid #f0f0f0",
    borderRadius: 16,
    padding: "28px 32px 32px",
    marginBottom: 20,
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  };

  const cardIconBadgeStyle = {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: "#f3e8ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };

  const cardHeaderStyle = {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: 20,
    paddingBottom: 20,
    borderBottom: "1px solid #eee",
  };

  const CardSectionTitle = ({ icon: Icon, title, subtitle }) => (
    <div style={cardHeaderStyle}>
      <div style={cardIconBadgeStyle}>
        <Icon style={{ color: "#470172", fontSize: 20 }} />
      </div>
      <div>
        <Typography.Text strong style={{ ...baseStyle, fontSize: 20, color: "#3C007A", display: "block" }}>
          {title}
        </Typography.Text>
        <Typography.Text style={{ ...baseStyle, fontSize: 13, color: "#888" }}>
          {subtitle}
        </Typography.Text>
      </div>
    </div>
  );

  const cardLabelStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "14px",
    fontWeight: 600,
    color: "#1a1a1a",
    marginBottom: "6px",
  };

  const cardInputStyle = { borderRadius: 10, padding: "8px 12px", height: 44 };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <div style={{ padding: isAdminEdit ? "0" : "20px", maxWidth: "900px", margin: "0 auto" }}>
      {!isAdminEdit && (
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <Typography.Title level={2} style={{ ...baseStyle, fontSize: "28px", fontWeight: 500, color: "#470172" }}>
            Investor Registration
          </Typography.Title>
          <Typography.Text style={{ ...baseStyle, fontSize: "16px", opacity: 0.7 }}>
            Join our platform and discover investment opportunities
          </Typography.Text>
        </div>
      )}

      <Card bordered style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(71, 1, 114, 0.10)", marginBottom: 32 }}>
        <form onSubmit={handleSubmit}>
        <Form layout="vertical" component={false} requiredMark={false}>
          {/* Investor Type Toggle */}
          <div style={sectionStyle}>
            <SectionTitle icon={TeamOutlined}>Investor Type</SectionTitle>
            <Radio.Group
              value={formData.investorType}
              onChange={(e) => setFormData(prev => ({ ...prev, investorType: e.target.value }))}
              style={{ width: '100%' }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Radio.Button value="individual" style={{ width: '100%', height: 'auto', padding: '16px', textAlign: 'center' }}>
                    <UserOutlined style={{ fontSize: 24, display: 'block', marginBottom: 8 }} />
                    <div style={{ fontSize: 16, fontWeight: 500 }}>Individual Investor</div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Personal investments</div>
                  </Radio.Button>
                </Col>
                <Col span={12}>
                  <Radio.Button value="company" style={{ width: '100%', height: 'auto', padding: '16px', textAlign: 'center' }}>
                    <BankOutlined style={{ fontSize: 24, display: 'block', marginBottom: 8 }} />
                    <div style={{ fontSize: 16, fontWeight: 500 }}>Investment Firm</div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Company investments</div>
                  </Radio.Button>
                </Col>
              </Row>
            </Radio.Group>
          </div>

          {/* Logo/Photo Upload */}
          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              {formData.investorType === 'individual' ? (
                <UserOutlined style={{ color: "#470172", fontSize: 18 }} />
              ) : (
                <BankOutlined style={{ color: "#470172", fontSize: 18 }} />
              )}
              <Typography.Text strong style={{ ...baseStyle, fontSize: 17, color: "#470172" }}>
                {formData.investorType === 'individual' ? 'Profile Photo' : 'Company Logo'}
              </Typography.Text>
            </div>
            <Form.Item
              style={{ marginBottom: 0 }}
              extra={<Typography.Text style={{ fontSize: 12, color: '#999' }}>Upload {formData.investorType === 'individual' ? 'photo' : 'logo'} (Max 5MB)</Typography.Text>}
            >
              <Upload
                listType="picture-card"
                showUploadList={false}
                beforeUpload={() => false}
                accept="image/*"
                maxCount={1}
                onChange={(info) => {
                  const file = info.fileList[0]?.originFileObj;
                  if (file) {
                    handleChange({ target: {
                      name: formData.investorType === 'individual' ? 'profilePicture' : 'companyLogo',
                      files: [file],
                      type: 'file'
                    }});
                  }
                }}
              >
                {(formData.investorType === 'individual' ? profilePreview : logoPreview) ? (
                  <img
                    src={formData.investorType === 'individual' ? profilePreview : logoPreview}
                    alt={formData.investorType === 'individual' ? "profile" : "logo"}
                    style={{ width: '100%', height: 120, objectFit: 'contain', borderRadius: 8 }}
                  />
                ) : (
                  uploadButton
                )}
              </Upload>
            </Form.Item>
          </div>

          {/* Personal/Company Information */}
          <div style={cardSectionStyle}>
            <CardSectionTitle
              icon={formData.investorType === 'individual' ? UserOutlined : BankOutlined}
              title={formData.investorType === 'individual' ? 'Personal Information' : 'Company Information'}
              subtitle={formData.investorType === 'individual' ? 'Please provide your personal details.' : 'Please provide your company details.'}
            />

          <Row gutter={20}>
            {formData.investorType === 'individual' ? (
              <>
                <Col span={12}>
                  <Form.Item label={<span style={cardLabelStyle}>Full Name <span style={{ color: 'red' }}>*</span></span>}>
                    <Input
                      prefix={<UserOutlined style={{ color: "#470172" }} />}
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      required
                      style={cardInputStyle}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={<span style={cardLabelStyle}>Professional Title</span>}>
                    <Input
                      prefix={<TeamOutlined style={{ color: "#470172" }} />}
                      name="designation"
                      value={formData.designation}
                      onChange={handleChange}
                      placeholder="e.g., Angel Investor, VC Partner"
                      style={cardInputStyle}
                    />
                  </Form.Item>
                </Col>
              </>
            ) : (
              <>
                <Col span={12}>
                  <Form.Item label={<span style={cardLabelStyle}>Company Name <span style={{ color: 'red' }}>*</span></span>}>
                    <Input
                      prefix={<BankOutlined style={{ color: "#470172" }} />}
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="Enter company name"
                      required
                      style={cardInputStyle}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={<span style={cardLabelStyle}>Registration Number</span>}>
                    <Input
                      prefix={<FileTextOutlined style={{ color: "#470172" }} />}
                      name="registrationNumber"
                      value={formData.registrationNumber}
                      onChange={handleChange}
                      placeholder="Registration number"
                      style={cardInputStyle}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={<span style={cardLabelStyle}>Year Founded</span>}>
                    <Select
                      value={formData.yearFounded}
                      onChange={(value) => setFormData(prev => ({ ...prev, yearFounded: value }))}
                      style={{ width: '100%' }}
                      className="investor-card-select"
                      suffixIcon={<CalendarOutlined />}
                    >
                      {yearOptions.map(year => (
                        <Select.Option key={year} value={year}>{year}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </>
            )}

            <Col span={12}>
              <Form.Item label={<span style={cardLabelStyle}>Email Address <span style={{ color: 'red' }}>*</span></span>}>
                <Input
                  prefix={<MailOutlined style={{ color: "#470172" }} />}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                  style={cardInputStyle}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={<span style={cardLabelStyle}>Phone / Mobile <span style={{ color: 'red' }}>*</span></span>}>
                <Input
                  prefix={<PhoneOutlined style={{ color: "#470172" }} />}
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 000-0000"
                  required
                  style={cardInputStyle}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={<span style={cardLabelStyle}>Website</span>}>
                <Input
                  prefix={<GlobalOutlined style={{ color: "#470172" }} />}
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://yourwebsite.com"
                  style={cardInputStyle}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={<span style={cardLabelStyle}>Country</span>}>
                <Input
                  prefix={<GlobalOutlined style={{ color: "#470172" }} />}
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="e.g., India"
                  style={cardInputStyle}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label={<span style={cardLabelStyle}>City</span>}>
                <Input
                  prefix={<EnvironmentOutlined style={{ color: "#470172" }} />}
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="e.g., New York"
                  style={cardInputStyle}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label={<span style={cardLabelStyle}>Address</span>} style={{ marginBottom: 20 }}>
                <Input.TextArea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Street address"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  style={{ borderRadius: 10, padding: "10px 12px" }}
                />
              </Form.Item>
            </Col>
          </Row>

          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: "#faf9fb",
            border: "1px solid #f0eef2",
            borderRadius: 10,
            padding: "12px 16px",
          }}>
            <SafetyOutlined style={{ color: "#470172", fontSize: 16 }} />
            <Typography.Text style={{ ...baseStyle, fontSize: 13, color: "#666" }}>
              Your information is secure and will not be shared with anyone.
            </Typography.Text>
          </div>
          </div>

          {/* Investment Preferences */}
          <div style={sectionStyle}>
            <SectionTitle icon={DollarOutlined}>Investment Preferences</SectionTitle>

          <Form.Item label={<span style={labelStyle}>Investment Stage (Select all that apply)</span>} style={{ marginBottom: 20 }}>
            <Checkbox.Group 
              value={formData.investmentStage}
              onChange={(values) => setFormData(prev => ({ ...prev, investmentStage: values }))}
              style={{ width: '100%' }}
            >
              <Row gutter={[8, 8]}>
                {investmentStageOptions.map(stage => (
                  <Col span={8} key={stage}>
                    <Checkbox value={stage}>{stage}</Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item label={<span style={labelStyle}>Typical Investment Size (Ticket Size)</span>} style={{ marginBottom: 20 }}>
            <Select
              value={formData.ticketSize}
              onChange={(value) => setFormData(prev => ({ ...prev, ticketSize: value }))}
              placeholder="Select typical investment size"
              style={{ width: '100%', borderRadius: 6 }}
              suffixIcon={<DollarOutlined />}
            >
              {ticketSizeOptions.map(size => (
                <Select.Option key={size} value={size}>{size}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label={<span style={labelStyle}>Preferred Industries (Select up to 5)</span>} style={{ marginBottom: 20 }}>
            <Checkbox.Group 
              value={formData.preferredIndustries}
              onChange={(values) => {
                if (values.length <= 5) {
                  setFormData(prev => ({...prev, preferredIndustries: values}));
                } else {
                  toast.warning('Maximum 5 industries can be selected');
                }
              }}
              style={{ width: '100%' }}
            >
              <Row gutter={[8, 8]}>
                {Array.isArray(industries) && industries.length > 0 ? (
                  industries.slice(0, 15).map((industry) => {
                    const industryId = industry?._id || industry;
                    const industryName = industry?.sectorName || industry?.name || industry;
                    const isDisabled = formData.preferredIndustries.length >= 5 && !formData.preferredIndustries.includes(industryId);
                    return (
                      <Col span={8} key={industryId}>
                        <Checkbox value={industryId} disabled={isDisabled}>{industryName}</Checkbox>
                      </Col>
                    );
                  })
                ) : (
                  <Col span={24}><Typography.Text>Loading industries...</Typography.Text></Col>
                )}
              </Row>
            </Checkbox.Group>
            {formData.preferredIndustries.length >= 5 && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Maximum of 5 industries selected</Typography.Text>
            )}
          </Form.Item>
          </div>

          {/* About Section */}
          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <FileTextOutlined style={{ color: "#470172", fontSize: 18 }} />
              <Typography.Text strong style={{ ...baseStyle, fontSize: 17, color: "#470172" }}>
                About {formData.investorType === 'individual' ? 'You' : 'Your Company'}
              </Typography.Text>
            </div>

          <Form.Item label={<span style={labelStyle}>
            {formData.investorType === 'individual' ? 'Tell us about yourself and your investment experience' : 'Company Description'}
          </span>} style={{ marginBottom: 0 }}
            extra={<Typography.Text type="secondary" style={{ fontSize: 12 }}>This will be displayed on your public profile</Typography.Text>}
          >
            <Input.TextArea
              name="about"
              value={formData.about}
              onChange={handleChange}
              rows={5}
              placeholder={formData.investorType === 'individual'
                ? 'Your background, investment experience, and what you look for in opportunities...'
                : 'Company overview, investment philosophy, and team background...'}
              style={{ borderRadius: 6 }}
            />
          </Form.Item>
          </div>

          {/* Verification Document */}
          <div style={sectionStyle}>
            <SectionTitle icon={FileTextOutlined}>Verification</SectionTitle>

            <Form.Item
              label={<span style={labelStyle}>
                {formData.investorType === 'individual' ? 'ID Verification' : 'Business Registration'} <span style={{ color: 'red' }}>*</span>
              </span>}
              style={{ marginBottom: 0 }}
              extra={<Typography.Text type="secondary" style={{ fontSize: 12 }}>PDF, JPG, or PNG up to 10MB</Typography.Text>}
            >
              <Upload
                maxCount={1}
                beforeUpload={() => false}
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(info) => {
                  const file = info.fileList[0]?.originFileObj;
                  if (file) {
                    handleChange({ target: { name: 'identificationDocument', files: [file], type: 'file' }});
                  }
                }}
              >
                <Button icon={<FileTextOutlined />}>
                  {documentName || `Upload ${formData.investorType === 'individual' ? 'ID Document' : 'Registration Document'}`}
                </Button>
              </Upload>
            </Form.Item>
          </div>

          {/* Account Security */}
          {!isAdminEdit && (
            <div style={sectionStyle}>
              <SectionTitle icon={LockOutlined}>Account Security</SectionTitle>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label={<span style={labelStyle}>Password <span style={{ color: 'red' }}>*</span></span>}>
                    <Input.Password 
                      prefix={<LockOutlined />} 
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create password"
                      required
                      minLength={6}
                      style={{ borderRadius: 6 }}
                    />
                    {formData.password && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <div style={{ flex: 1, height: 6, background: '#eee', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ 
                              height: '100%', 
                              width: passwordStrength === "strong" ? '100%' : passwordStrength === "medium" ? '66%' : '33%',
                              background: passwordStrength === "strong" ? '#52c41a' : passwordStrength === "medium" ? '#faad14' : '#ff4d4f',
                              transition: 'all 0.3s'
                            }} />
                          </div>
                          <Typography.Text style={{ 
                            fontSize: 12,
                            color: passwordStrength === "strong" ? '#52c41a' : passwordStrength === "medium" ? '#faad14' : '#ff4d4f'
                          }}>
                            {passwordStrength === "strong" ? 'Strong' : passwordStrength === "medium" ? 'Medium' : 'Weak'}
                          </Typography.Text>
                        </div>
                      </div>
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={<span style={labelStyle}>Confirm Password <span style={{ color: 'red' }}>*</span></span>}>
                    <Input.Password 
                      prefix={<LockOutlined />} 
                      name="password2"
                      value={formData.password2}
                      onChange={handleChange}
                      placeholder="Re-enter password"
                      required
                      minLength={6}
                      style={{ borderRadius: 6 }}
                    />
                    {formData.password2 && (
                      <Typography.Text style={{ 
                        fontSize: 12, 
                        color: formData.password === formData.password2 ? '#52c41a' : '#ff4d4f',
                        display: 'block',
                        marginTop: 8
                      }}>
                        {formData.password === formData.password2 ? '✓ Passwords match' : '✗ Passwords do not match'}
                      </Typography.Text>
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </div>
          )}

          {!isAdminEdit && (
            <Form.Item style={{ marginBottom: 20 }}>
              <Checkbox required>
                I agree to the <a href="/terms" style={{ color: '#470172' }}>Terms of Service</a> and <a href="/privacy" style={{ color: '#470172' }}>Privacy Policy</a>
              </Checkbox>
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0 }}>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              block
              size="large"
              style={{ 
                background: 'linear-gradient(to right, #672f89, #8e44ad)', 
                borderColor: '#672f89',
                borderRadius: 8,
                height: 48,
                fontSize: 16,
                fontWeight: 500
              }}
            >
              {loading ? 'Creating Account...' : `Create ${formData.investorType === 'individual' ? 'Investor' : 'Investment Firm'} Account`}
            </Button>
          </Form.Item>

          {!isAdminEdit && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Typography.Text>
                Already have an account? <a href="/login" style={{ color: '#470172' }}>Sign in here</a>
              </Typography.Text>
            </div>
          )}
        </Form>
        </form>
      </Card>
    </div>
  );
};

export default InvestorRegister;